import React, { useEffect, useRef, useState } from "react";
import s from "./style.module.css";
import Vizualisation from "../../../../components/Vizualisation";
import useVizualisation from "../../../../helpers/useVizualisation";
import SPI_index from "../../data/SPI_index.json";
import { forceSimulation, forceY, forceX, forceCollide } from "d3-force";
import { scaleLinear, scaleOrdinal } from "d3-scale";
import { select } from "d3-selection";
import { range } from "d3-array";

const margin = {
  top: 10,
  right: 50,
  bottom: 10,
  left: 50,
};

const pathString =
  range(6)
    .map((d) => [
      Math.cos((d / 6) * Math.PI * 2) * 5,
      Math.sin((d / 6) * Math.PI * 2) * 5,
    ])
    .reduce(
      (acc, cur, i) => acc + `${i === 0 ? "M" : "L"}${cur[0]},${cur[1]} `,
      ""
    ) + "Z";

const BeeSwarm = ({
  data,
  title,
  note,
  source,
  spi_index,
  subTitle,
  highlight,
  legendText,
  countries,
}) => {
  const { ref, width, windowSize, inView } = useVizualisation();
  const height = 300;

  const SPI = spi_index;

  const nodes = useRef(null);
  const nodeHovers = useRef(null);

  const w = 950 - margin.left - margin.right;
  const x = scaleLinear().domain([0, 100]).range([0, w]);

  const selectedNodes = SPI_index.filter(
    (d) => d.date === 2019 && d[SPI] !== "NA"
  ).map((d) => ({
    ...d,
    x: Math.random() * w,
    y: 0,
  }));

  useEffect(() => {
    const updateNode = (selection) => {
      selection.attr("transform", (d) => {
        if (d) {
          return `translate(${d.x}, ${d.y})`;
        }
      });
    };

    let simulation = new forceSimulation(selectedNodes)
      .force(
        "x",
        forceX().x((d) => x(d[SPI] === "NA" ? 0 : d[SPI]))
      )
      .force("y", forceY().y(0))
      .force("collision", forceCollide().radius(5))
      .on("tick", () => {
        select(nodes.current).selectAll(".node").call(updateNode);

        select(nodeHovers.current).selectAll(".node-hover").call(updateNode);
      });

    return () => {
      simulation.stop();
    };
  }, [w, selectedNodes, SPI, x]);

  const c = (d) => (d === "Low income" ? "#EE6047" : "#cccccf");

  const NodeHover = (props) => {
    const nodeHover = useRef(null);

    const [hover, setHover] = useState(false);

    const enterNode = (selection) => {
      selection.select("circle");
    };

    useEffect(() => {
      select(nodeHover.current).datum(props.data).call(enterNode);
    });

    return (
      <g
        ref={nodeHover}
        className="node-hover"
        onMouseOver={() => setHover(true)}
        onMouseOut={() => setHover(false)}
      >
        {hover && (
          <g className={s.countryHighlight}>
            <circle r={8} />
            <line y1={-8} y2={-40} />
            <text
              dy={-45}
              fill="white"
              stroke="white"
              style={{
                strokeWidth: 2,
                textAnchor: "middle",
                fontSize: width < 600 ? 24 : 14,
              }}
            >
              {props.countryLabel}
            </text>
            <text
              style={{
                textAnchor: "middle",
                fontSize: width < 600 ? 24 : 14,
              }}
              dy={-45}
            >
              {props.countryLabel}
            </text>
          </g>
        )}
        <circle r={8} fill="transparent" />
      </g>
    );
  };

  const Node = (props) => {
    const node = useRef(null);

    const enterNode = (selection) => {
      selection.select("circle");
    };

    useEffect(() => {
      select(node.current).datum(props.data).call(enterNode);
    });

    return (
      <g ref={node} className="node">
        {props.data.iso3c === highlight && (
          <g className={s.countryHighlight}>
            <circle r={8} />
            <line y1={-8} y2={-40} />
            <text
              dy={-45}
              fill="white"
              stroke="white"
              style={{
                strokeWidth: 2,
                textAnchor: "middle",
                fontSize: width < 600 ? 24 : 14,
              }}
            >
              {props.data.country}
            </text>
            <text
              style={{ textAnchor: "middle", fontSize: width < 600 ? 24 : 14 }}
              dy={-45}
            >
              {props.countryLabel}
            </text>
          </g>
        )}
        <path
          d={pathString}
          stroke={
            props.data.iso3c === highlight ? "#F2C118" : c(props.data.income)
          }
          fill="white"
        />
        <circle
          r={3}
          fill={
            props.data.iso3c === highlight ? "#F2C118" : c(props.data.income)
          }
        />
      </g>
    );
  };

  return (
    <Vizualisation
      title={title}
      note={note}
      source={source}
      ref={ref}
      size="large"
    >
      <svg viewBox="0 0 950 250">
        <g transform={`translate(${margin.left}, ${margin.top})`}>
          <g transform={`translate(0, ${height / 2})`}>
            <line x2={w} stroke="#ccc" />
            {[0, 20, 40, 60, 80, 100].map((d) => (
              <g transform={`translate(${x(d)}, 0)`} key={`tick-${d}`}>
                <line y2={-height / 3} stroke="#ccc" />
                <text
                  y={-height / 3}
                  dy={-7}
                  style={{
                    textAnchor: "middle",
                    fontSize: width < 600 ? 18 : 12,
                  }}
                >
                  {d}
                </text>
              </g>
            ))}
            <g ref={nodes} className="nodes">
              {selectedNodes.map((d) => (
                <Node
                  data={d}
                  key={d.iso3c}
                  countryLabel={countries[d.iso3c]}
                />
              ))}
            </g>
            <g ref={nodeHovers} className="nodesHovers">
              {selectedNodes.map((d) => (
                <NodeHover
                  data={d}
                  key={d.iso3c}
                  countryLabel={countries[d.iso3c]}
                />
              ))}
            </g>
            <g transform="translate(0, 50)" className={s.legend}>
              <path
                d={
                  range(6)
                    .map((d) => [
                      Math.cos((d / 6) * Math.PI * 2) * 5,
                      Math.sin((d / 6) * Math.PI * 2) * 5,
                    ])
                    .map((d, i) => `${i === 0 ? "M" : "L"}${d[0]},${d[1]} `) +
                  "Z"
                }
                stroke="#CD546C"
                fill="white"
              />
              <circle r={3} fill="#CD546C" />
              <text dy={0} dx={10} style={{ fontSize: width < 600 ? 24 : 14 }}>
                {" "}
                = {legendText}
              </text>
            </g>
          </g>
        </g>
      </svg>
    </Vizualisation>
  );
};

export default BeeSwarm;
